import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu'
import { InquiryStageEnum } from '@/gen/cortado/v1/settings_pb'
import { getInquiryStageColor } from '@/lib/enum-utils'

interface StageSelectorDropdownProps {
  selectedStages: number[]
  onToggleStage: (stage: number) => void
  align?: 'start' | 'center' | 'end'
  asSubContent?: boolean
}

export const STAGE_OPTIONS = [
  { value: InquiryStageEnum.INQUIRY_STAGE_NEW, label: 'New' },
  { value: InquiryStageEnum.INQUIRY_STAGE_IN_PROGRESS, label: 'In Progress' },
  { value: InquiryStageEnum.INQUIRY_STAGE_QUOTE_SENT, label: 'Quote Sent' },
  { value: InquiryStageEnum.INQUIRY_STAGE_ACCEPTED, label: 'Accepted' },
  { value: InquiryStageEnum.INQUIRY_STAGE_REJECTED, label: 'Rejected' },
  { value: InquiryStageEnum.INQUIRY_STAGE_STALE, label: 'Stale' },
  { value: InquiryStageEnum.INQUIRY_STAGE_REFERRED, label: 'Referred' },
] as const

/**
 * Reusable dropdown content for selecting inquiry stages with checkboxes.
 *
 * Usage with DropdownMenu:
 * ```tsx
 * <DropdownMenu>
 *   <DropdownMenuTrigger>Select Stages</DropdownMenuTrigger>
 *   <StageSelectorDropdown
 *     selectedStages={selectedStages}
 *     onToggleStage={toggleStage}
 *   />
 * </DropdownMenu>
 * ```
 *
 * Usage with DropdownMenuSub:
 * ```tsx
 * <DropdownMenuSub>
 *   <DropdownMenuSubTrigger>Stage</DropdownMenuSubTrigger>
 *   <StageSelectorDropdown
 *     selectedStages={selectedStages}
 *     onToggleStage={toggleStage}
 *     asSubContent
 *   />
 * </DropdownMenuSub>
 * ```
 */
export function StageSelectorDropdown({
  selectedStages,
  onToggleStage,
  align = 'start',
  asSubContent = false,
}: StageSelectorDropdownProps) {
  const items = STAGE_OPTIONS.map((option) => {
    const isSelected = selectedStages.includes(option.value)
    return (
      <DropdownMenuItem
        key={option.value}
        onSelect={(e) => {
          e.preventDefault()
          onToggleStage(option.value)
        }}
        className="cursor-pointer"
      >
        <div className="flex items-center gap-2 w-full">
          <Checkbox
            checked={isSelected}
            className="data-[state=checked]:bg-stone-300 data-[state=checked]:border-stone-300 data-[state=checked]:text-stone-900"
          />
          <Badge variant={getInquiryStageColor(option.value)}>
            {option.label}
          </Badge>
        </div>
      </DropdownMenuItem>
    )
  })

  if (asSubContent) {
    return <DropdownMenuSubContent className="w-[200px]">{items}</DropdownMenuSubContent>
  }

  return (
    <DropdownMenuContent align={align} className="w-[200px]">
      {items}
    </DropdownMenuContent>
  )
}
